import React, {useState, useEffect, useCallback} from 'react';
import {getForm} from './api/forms';
import FormItem from './FormItem';

// 检测是否为移动设备
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth <= 768;
};

export default function FormFillerDisplay({formId, onFormDataLoaded, formData, previewMode, cardNumber}) {
    const [formSchema, setFormSchema] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [formName, setFormName] = useState('表单展示');
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const [error, setError] = useState('');

    // 检测移动设备
    useEffect(() => {
        setIsMobileDevice(isMobile());
    }, []);

    // 表单加载函数
    const loadForm = useCallback(async (formId) => {
        try {
            console.log(`🔄 FormFillerDisplay: 开始加载表单 ${formId}`);
            const res = await getForm(formId);
            
            console.log('📋 FormFillerDisplay: 表单数据响应:', res);
            
            // 解析表单数据
            let schema = null;
            let wechatShare = null;
            
            if (res.data && res.data.formSchema) {
                schema = typeof res.data.formSchema === 'string' ? JSON.parse(res.data.formSchema) : res.data.formSchema;
            } else if (res.formSchema) {
                schema = typeof res.formSchema === 'string' ? JSON.parse(res.formSchema) : res.formSchema;
            }
            
            if (res.data && res.data.wechatShare) {
                wechatShare = typeof res.data.wechatShare === 'string' ? JSON.parse(res.data.wechatShare) : res.data.wechatShare;
            } else if (res.wechatShare) {
                wechatShare = typeof res.wechatShare === 'string' ? JSON.parse(res.wechatShare) : res.wechatShare;
            }
            
            if (schema) {
                console.log('✅ FormFillerDisplay: 表单数据解析成功');
                setFormName(schema.title || schema.formTitle || '表单展示');
                setFormSchema({
                    title: schema.title || schema.formTitle,
                    formTitle: schema.title || schema.formTitle,
                    desc: schema.formDesc || schema.description,
                    formDesc: schema.formDesc || schema.description,
                    components: schema.components || [],
                    showTitle: schema.showTitle !== false,
                    formMessage: schema.formMessage || '提交成功',
                    region: res.data?.region || schema.region
                });
                
                // 初始化表单默认值
                const initialValues = {};
                (schema.components || []).forEach(comp => {
                    initialValues[comp.id] = comp.defaultValue ?? '';
                });
                
                // 如果有卡号参数，预填充到相关字段
                if (cardNumber) {
                    (schema.components || []).forEach(comp => {
                        // 优先检查组件类型是否为 vip_card_number
                        if (comp.type === 'vip_card_number') {
                            initialValues[comp.id] = cardNumber;
                            console.log(`预填充卡号到vip_card_number字段: ${comp.label || comp.id}`);
                            return;
                        }
                        
                        // 如果没有vip_card_number类型，则使用原有的匹配逻辑
                        const fieldLabel = comp.label || comp.fieldName || '';
                        const fieldId = comp.id || '';
                        
                        if (fieldLabel.includes('卡号') || fieldLabel.includes('卡') || 
                            fieldId.includes('card') || fieldId.includes('number') ||
                            fieldLabel.includes('特权卡') || fieldLabel.includes('黑卡')) {
                            initialValues[comp.id] = cardNumber;
                            console.log(`预填充卡号到字段: ${fieldLabel} (${fieldId})`);
                        }
                    });
                }
                
                setFormValues(initialValues);
                
                // 回调父组件
                if (onFormDataLoaded) {
                    onFormDataLoaded({
                        ...res.data,
                        formSchema: schema,
                        wechatShare: wechatShare
                    });
                }
                
                setError('');
                console.log('🎉 FormFillerDisplay: 表单加载完成');
            } else {
                throw new Error('表单数据格式错误');
            }
        } catch (error) {
            console.error('❌ FormFillerDisplay: 表单加载失败:', error);
            const errorMessage = error.message || '表单加载失败，请稍后重试';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [cardNumber, onFormDataLoaded]);

    useEffect(() => {
        if (previewMode && formData) {
            // 预览模式：直接使用传入的formData
            setFormName(formData.title || '表单展示');
            setFormSchema({
                title: formData.title,
                formTitle: formData.title,
                desc: formData.formDesc,
                formDesc: formData.formDesc,
                components: formData.components || [],
                showTitle: formData.showTitle !== false
            });

            // 初始化表单默认值
            const initialValues = {};
            (formData.components || []).forEach(comp => {
                initialValues[comp.id] = comp.defaultValue ?? '';
            });
            
            // 如果有卡号参数，预填充到相关字段
            if (cardNumber) {
                (formData.components || []).forEach(comp => {
                    // 优先检查组件类型是否为 vip_card_number
                    if (comp.type === 'vip_card_number') {
                        initialValues[comp.id] = cardNumber;
                        console.log(`预填充卡号到vip_card_number字段: ${comp.label || comp.id}`);
                        return;
                    }
                    
                    // 如果没有vip_card_number类型，则使用原有的匹配逻辑
                    const fieldLabel = comp.label || comp.fieldName || '';
                    const fieldId = comp.id || '';
                    
                    if (fieldLabel.includes('卡号') || fieldLabel.includes('卡') || 
                        fieldId.includes('card') || fieldId.includes('number') ||
                        fieldLabel.includes('特权卡') || fieldLabel.includes('黑卡')) {
                        initialValues[comp.id] = cardNumber;
                        console.log(`预填充卡号到字段: ${fieldLabel} (${fieldId})`);
                    }
                });
            }
            
            setFormValues(initialValues);
            setLoading(false);
            return;
        }

        if (!formId) return;
        
        setLoading(true);
        setError('');
        loadForm(formId);
    }, [formId, previewMode, formData, onFormDataLoaded, loadForm]);

    if (loading) return <div style={{textAlign: 'center', padding: 40}}>加载中...</div>;
    if (!formSchema) return <div style={{textAlign: 'center', padding: 40, color: '#999'}}>表单不存在</div>;

    // 优先使用formData.formDesc（预览弹窗传递），否则用formSchema.desc
    const previewDesc = (formData && formData.formDesc) || formSchema.desc || formSchema.formDesc;
    // 标题显示开关（兼容 "false" 字符串）
    const contentShowTitle = !(formSchema && ((formSchema.showTitle === false) || (String(formSchema.showTitle).toLowerCase() === 'false')));

    return (
        <div className="form-filler-display" style={{
            maxWidth: '100%',
            margin: '0 auto',
            padding: '12px',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxSizing: 'border-box',
            position: 'relative'
        }}>
            {/* 错误提示 */}
            {error && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px 24px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    width: '100%',
                    maxWidth: '400px',
                    textAlign: 'center',
                    zIndex: 10000
                }}>
                    <div style={{fontSize: '40px', color: '#ff7875', margin: '0 0 16px 0'}}>⚠️</div>
                    <div style={{fontSize: '18px', color: '#222', marginBottom: '12px', fontWeight: '600'}}>访问提示</div>
                    <div style={{fontSize: '14px', color: '#666', marginBottom: '24px', lineHeight: '1.5'}}>{error}</div>
                    <button
                        onClick={() => window.history.back()}
                        style={{
                            padding: '12px 24px',
                            background: '#1890ff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        返回
                    </button>
                </div>
            )}

            <div style={{
                maxWidth: '100%',
                margin: '0 auto',
                background: '#fff',
                overflow: 'hidden'
            }}>
                {/* 表单头部 */}
                <div style={{
                    background: '#fff',
                    color: '#333',
                    padding: '12px 8px',
                    textAlign: 'left'
                }}>
                    {contentShowTitle && (<h1 style={{
                        color: '#333',
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: '0 0 12px 0',
                        lineHeight: '1.3'
                    }}>
                        {(formSchema && (formSchema.title || formSchema.formTitle)) || '未命名表单'}
                    </h1>)}
                    {previewDesc && (
                        <div style={{
                            fontSize: '14px',
                            lineHeight: '1.6',
                            maxWidth: '100%',
                            margin: '0',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            padding: '0',
                            boxSizing: 'border-box',
                            textAlign: 'left',
                            position: 'relative'
                        }}>
                            <style>{`
                                .form-description img {
                                    width: 100% !important;
                                    max-width: 100% !important;
                                    height: auto !important;
                                    display: block;
                                    margin: 0 !important;
                                    padding: 0 !important;
                                    box-sizing: border-box;
                                }
                            `}</style>
                            <div className="form-description" dangerouslySetInnerHTML={{__html: previewDesc}}/>
                        </div>
                    )}
                </div>

                {/* 表单内容 - 只展示，不可编辑 */}
                <div style={{
                    padding: '12px 8px',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    {/* 移动端检测提示（仅开发环境显示） */}
                    {process.env.NODE_ENV === 'development' && (
                        <div style={{
                            background: isMobileDevice ? '#e6f7ff' : '#fff7e6',
                            border: isMobileDevice ? '1px solid #91d5ff' : '1px solid #ffd591',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginBottom: '16px',
                            fontSize: '12px',
                            color: isMobileDevice ? '#1890ff' : '#d46b08',
                            textAlign: 'center'
                        }}>
                            {isMobileDevice ? '📱 移动端模式' : '💻 桌面端模式'} - 展示模式
                        </div>
                    )}
                    
                    {(formSchema.components || []).map(comp => (
                        <div key={comp.id} style={{
                            marginBottom: '16px',
                            display: comp.hidden ? 'none' : 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                        }}>
                            {(comp.label || comp.fieldName || comp.id) && (
                                <label style={{
                                    display: 'block',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                    color: '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.3'
                                }}>
                                    {comp.label || comp.fieldName || comp.id}
                                    {comp.required && <span style={{color: '#ff7875', marginLeft: '3px'}}> *</span>}
                                </label>
                            )}
                            {/* 展示模式：只显示值，不可编辑 */}
                            <div style={{
                                padding: '8px 12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                backgroundColor: '#fafafa',
                                color: '#666',
                                fontSize: '14px',
                                minHeight: '32px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                {formValues[comp.id] || '（空）'}
                            </div>
                        </div>
                    ))}

                    {/* 展示模式提示 */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: '24px',
                        padding: '16px',
                        background: '#f0f9ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        color: '#1e40af',
                        fontSize: '14px'
                    }}>
                        📋 当前为表单展示模式，数据仅供查看
                    </div>
                </div>
            </div>
        </div>
    );
}
